import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, formatViews, getAnimeById, getRelated } from "@/utils/anime";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = getAnimeById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const saved = anime ? isFavorite(anime.id) : false;
  const related = useMemo(() => (anime ? getRelated(anime, 6) : []), [anime?.id]);

  usePageMeta({
    title: anime ? `${anime.title} - 色情动漫详情介绍` : "作品不存在 - 色情动漫",
    description: anime
      ? `${anime.title}（${anime.originalTitle}）是${anime.year}年${anime.region}${anime.category}类成人向动画作品，${anime.status}，共${anime.episodes}集。${anime.synopsis}`
      : "没有找到对应的作品资料，可能链接已失效或作品已下架。",
    keywords: anime
      ? `色情动漫,${anime.title},${anime.category}动漫,${anime.region}动漫,${anime.year}动漫,${anime.tags.join(",")}`
      : "色情动漫,作品详情",
  });

  useEffect(() => {
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-8 lg:px-12 py-20 md:py-28 flex flex-col items-center text-center">
          <p className="font-label text-6xl md:text-8xl text-primary-800 leading-none select-none">404</p>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl text-foreground-950">这部作品暂时不在这里</h1>
          <p className="mt-3 text-sm text-foreground-500 max-w-md leading-relaxed">
            链接可能已经失效，或者作品尚未收录。你可以回到分类页继续浏览，或用关键词搜索想看的作品。
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/category"
              className="h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm flex items-center transition-colors whitespace-nowrap"
            >
              返回分类页
            </Link>
            <Link
              to="/search"
              className="h-10 px-5 rounded-md border border-background-400/60 text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 text-sm flex items-center transition-colors whitespace-nowrap"
            >
              搜索作品
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const infoRows = [
    { label: "类型", value: anime.category },
    { label: "地区", value: anime.region },
    { label: "年份", value: `${anime.year}` },
    { label: "状态", value: anime.status },
    { label: "集数", value: `${anime.episodes} 集` },
    { label: "原作 / 导演", value: anime.author },
    { label: "制作公司", value: anime.studio },
    { label: "更新日期", value: formatDate(anime.updatedAt) },
  ];

  const handleFavorite = () => {
    const next = toggleFavorite(anime.id);
    setToast(next ? "已收藏到本地，仅保存在当前浏览器" : "已取消收藏");
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="relative w-full">
          <div className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
            <img
              src={anime.banner}
              alt={`${anime.title} 主视觉`}
              title={`${anime.title} ${anime.category} ${anime.year}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40" />
          </div>

          <div className="w-full px-4 md:px-8 lg:px-12 -mt-40 md:-mt-56 relative z-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/category" className="hover:text-accent-400 transition-colors">动漫分类</Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/category?category=${encodeURIComponent(anime.category)}`}
                className="hover:text-accent-400 transition-colors"
              >
                {anime.category}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-400 truncate max-w-[180px]">{anime.title}</span>
            </nav>

            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] lg:grid-cols-[260px_1fr] gap-6 md:gap-8 items-end">
              <div className="relative w-40 sm:w-48 md:w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
                <img
                  src={anime.cover}
                  alt={`${anime.title} 封面`}
                  title={`${anime.title} 封面 ${anime.year}`}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-2 left-2 inline-flex items-center h-6 px-2 rounded bg-background-50/85 backdrop-blur-sm text-[11px] text-accent-400 border border-accent-600/30">
                  {anime.category}
                </span>
              </div>

              <div className="pb-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-flex items-center h-6 px-2 rounded bg-primary-600/90 text-foreground-950 text-[11px] tracking-wider">
                    {anime.status}
                  </span>
                  <span className="inline-flex items-center gap-1 h-6 px-2 rounded border border-accent-600/50 text-accent-400 text-[11px]">
                    <i className="ri-star-fill text-[10px]" />
                    {anime.rating.toFixed(1)} 站内评分
                  </span>
                  <span className="inline-flex items-center gap-1 h-6 px-2 rounded bg-background-200/70 text-[11px] text-foreground-600">
                    <i className="ri-eye-line text-[10px]" />
                    {formatViews(anime.views)} 浏览
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide">{anime.title}</h1>
                <p className="mt-1.5 text-xs tracking-[0.18em] text-foreground-500 uppercase">{anime.originalTitle}</p>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600">
                  <span>{anime.region}</span>
                  <span className="w-px h-3 bg-background-400/60" />
                  <span>{anime.year}</span>
                  <span className="w-px h-3 bg-background-400/60" />
                  <span>{anime.episodes} 集</span>
                  <span className="w-px h-3 bg-background-400/60" />
                  <span>{formatDate(anime.updatedAt)} 更新</span>
                </div>

                <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-2xl">{anime.synopsis}</p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`h-11 px-6 rounded-md text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap ${
                      saved
                        ? "bg-accent-500 text-background-50 hover:bg-accent-400"
                        : "bg-primary-600 text-foreground-950 hover:bg-primary-500"
                    }`}
                  >
                    <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                    {saved ? "已收藏" : "收藏这部作品"}
                  </button>
                  <Link
                    to={`/category?category=${encodeURIComponent(anime.category)}`}
                    className="h-11 px-5 rounded-md border border-background-400/60 text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 text-sm flex items-center gap-2 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-layout-grid-line" />
                    同分类作品
                  </Link>
                </div>
                <p className="mt-2.5 text-[11px] text-foreground-400">收藏为前端演示功能，仅保存在当前浏览器本地存储中。</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <article>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-accent-500 rounded-full" />
                剧情简介
              </h2>
              <div className="mt-4 flex flex-col gap-4 text-sm text-foreground-600 leading-7">
                {anime.description.split("\n\n").map((paragraph) => (
                  <p key={paragraph.slice(0, 12)}>{paragraph}</p>
                ))}
              </div>

              <h2 className="mt-10 font-heading text-lg md:text-xl text-foreground-950 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-accent-500 rounded-full" />
                作品标签
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {anime.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?query=${encodeURIComponent(tag)}`}
                    className="h-8 px-3.5 rounded-full bg-secondary-900/60 border border-background-300/50 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-10 rounded-lg border border-background-300/50 bg-background-100/60 p-5">
                <h2 className="text-sm text-foreground-900 tracking-wide flex items-center gap-2">
                  <i className="ri-information-line text-accent-500" />
                  内容说明
                </h2>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  本站仅整理作品资料、类型标签与更新信息，不提供任何影片播放、下载或付费入口。
                  作品内容与分级请以官方发行方说明为准，未满 18 岁请勿继续浏览。
                </p>
              </div>
            </article>

            <aside className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-300/50 bg-background-100/70 p-5">
                <h2 className="text-sm text-foreground-900 tracking-wide mb-4">作品资料</h2>
                <dl className="flex flex-col">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-3 py-2.5 border-b border-background-200/60 last:border-b-0">
                      <dt className="text-xs text-foreground-500 shrink-0">{row.label}</dt>
                      <dd className="text-xs text-foreground-800 text-right">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg border border-accent-700/40 bg-accent-500/5 p-5">
                <p className="text-[11px] tracking-[0.25em] text-accent-500 mb-2">EDITOR'S NOTE</p>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  本作在站内热度排名靠前，画面风格统一，适合按顺序观看。若你喜欢本作的气质，右侧推荐列表中的作品也值得一看。
                </p>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12 border-t border-background-200/60 bg-background-100/40">
          <SectionHeader
            eyebrow="YOU MAY ALSO LIKE"
            title="同类作品推荐"
            description="根据分类、标签与地区相似度生成的推荐，点击卡片可继续查看详情。"
            linkTo="/popular"
            linkLabel="查看热门榜单"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-7">
            {related.map((item) => (
              <AnimeCard key={item.id} anime={item} />
            ))}
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-up">
          <div className="h-10 px-4 rounded-md bg-background-100 border border-accent-600/50 text-xs text-foreground-800 flex items-center gap-2">
            <i className="ri-bookmark-line text-accent-500" />
            {toast}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}